import { AppState } from '@/types';
import { AppAction } from '../types';
import { createNewPet, updatePetWithPoints, givePetAttention, updatePetHappiness, consumePetPortion, feedPetWithFood, earnActivityCredits } from '@/lib/simplePetSystem';
import { SimplePet, getNextStage, PET_STAGES } from '@/types/simplePet';
import { 
  linkPetToPledgeSheet, 
  updatePetWithPledgePerformance, 
  processWeeklyPetSettlement as processSettlement 
} from '@/lib/pledgeBasedPetSystem';
import { PledgeBasedTarget } from '@/types/pledgeBasedPlan';

export function handleCreateSimplePet(
  state: AppState,
  action: Extract<AppAction, { type: 'CREATE_SIMPLE_PET' }>
): AppState {
  const userId = state.user?.id || 'default_user';
  const pet = createNewPet(userId, action.payload.name);
  
  return {
    ...state,
    simplePet: pet
  };
}

export function handleGivePetAttention(state: AppState): AppState {
  if (!state.simplePet) return state;
  
  const updatedPet = givePetAttention(state.simplePet);
  
  return {
    ...state,
    simplePet: updatedPet
  };
}

export function handleEvolveSimplePet(state: AppState): AppState {
  if (!state.simplePet) return state;
  
  const nextStage = getNextStage(state.simplePet.stage);
  if (!nextStage) return state;
  
  const evolvedPet: SimplePet = {
    ...state.simplePet,
    stage: nextStage,
    emoji: PET_STAGES[nextStage].emoji,
    happiness: 100, // Full happiness on evolution
    lastInteraction: new Date()
  };
  
  return {
    ...state,
    simplePet: evolvedPet
  };
}

export function handleUpdateSimplePetPoints(
  state: AppState,
  action: Extract<AppAction, { type: 'UPDATE_SIMPLE_PET_POINTS' }>
): AppState {
  if (!state.simplePet) return state;
  
  let updatedPet = updatePetWithPoints(state.simplePet, action.payload.points);
  // Also earn activity credits from activity points
  updatedPet = earnActivityCredits(updatedPet, action.payload.points);
  
  return {
    ...state,
    simplePet: updatedPet
  };
}

export function handleLinkPetToPledgeSheet(
  state: AppState,
  action: Extract<AppAction, { type: 'LINK_PET_TO_PLEDGE_SHEET' }>
): AppState {
  if (!state.simplePet) return state;
  
  const linkedPet = linkPetToPledgeSheet(state.simplePet, action.payload.pledgeSheetId);
  
  return {
    ...state,
    simplePet: linkedPet
  };
}

export function handleUpdatePetDailyPerformance(
  state: AppState,
  action: Extract<AppAction, { type: 'UPDATE_PET_DAILY_PERFORMANCE' }>
): AppState {
  if (!state.simplePet) return state;
  
  const updatedPet = updatePetWithPledgePerformance(
    state.simplePet,
    action.payload.weeklyTargets,
    state.activities
  );
  
  return {
    ...state,
    simplePet: updatedPet
  };
}

export function handleProcessWeeklyPetSettlement(
  state: AppState,
  action: Extract<AppAction, { type: 'PROCESS_WEEKLY_PET_SETTLEMENT' }>
): AppState {
  if (!state.simplePet) return state;
  
  const settledPet = processSettlement(state.simplePet, action.payload.weeklyTargets);
  
  return {
    ...state,
    simplePet: settledPet
  };
}

export function handleCheckPetDailyUpdate(
  state: AppState,
  action: Extract<AppAction, { type: 'CHECK_PET_DAILY_UPDATE' }>
): AppState {
  if (!state.simplePet) return state;
  
  // Check if pet needs daily update and apply if needed
  const updatedPet = updatePetWithPledgePerformance(
    state.simplePet,
    action.payload.weeklyTargets,
    state.activities
  );
  
  return {
    ...state,
    simplePet: updatedPet
  };
}

export function handleConsumePetPortion(state: AppState): AppState {
  if (!state.simplePet) return state;
  const updatedPet = consumePetPortion(state.simplePet);
  return {
    ...state,
    simplePet: updatedPet,
  };
}

export function handleCustomizeSimplePet(
  state: AppState,
  action: Extract<AppAction, { type: 'CUSTOMIZE_SIMPLE_PET' }>
): AppState {
  if (!state.simplePet) return state;

  const { hat, accessory } = action.payload;

  const updatedPet: SimplePet = {
    ...state.simplePet,
    ...(hat !== undefined ? { hat } : {}),
    ...(accessory !== undefined ? { accessory } : {}),
    lastInteraction: new Date(),
  };

  return {
    ...state,
    simplePet: updatedPet,
  };
}

export function handleFeedSimplePet(
  state: AppState,
  action: Extract<AppAction, { type: 'FEED_SIMPLE_PET' }>
): AppState {
  if (!state.simplePet) return state;

  const updatedPet = feedPetWithFood(state.simplePet, action.payload.foodKey);

  return {
    ...state,
    simplePet: updatedPet,
  };
}